import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Loader2, Search, SlidersHorizontal, LayoutGrid, List, Package, LogOut, Euro, BarChart3, Plus, ChevronLeft, X } from 'lucide-react';
import { useToast } from "../hooks/use-toast";
import { API_BASE_URL } from '../config';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Button } from "../components/ui/button";
import ProductForm from '../components/ProductFormComponent';

// ========== UTILITIES ==========
const displayValue = (value, placeholder = "Not set") => 
  value || value === 0 ? value : placeholder;

// ========== PRODUCT SERVICE ==========
const productService = {
  async fetchProducts() {
    const response = await fetch(`${API_BASE_URL}/api/product/list`, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('Failed to fetch products');
    }

    const data = await response.json();
    return data.products || [];
  }
};

// ========== PRODUCT FILTERS & SORTING ==========
// Enhanced filter function with multiple filter options
const filterProducts = (products, filters) => {
  const { searchQuery, availability, hasImage, model, status } = filters;
  
  return products.filter(product => {
    // Search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = 
        product.title?.toLowerCase().includes(searchLower) ||
        product.short_description?.toLowerCase().includes(searchLower) ||
        product.netlab_id?.toLowerCase().includes(searchLower) ||
        product.sku?.toLowerCase().includes(searchLower);
      
      if (!matchesSearch) return false;
    }
    
    // Availability filter
    if (availability !== 'all') {
      const isAvailable = product.available_inventory > 0;
      if (availability === 'available' && !isAvailable) return false;
      if (availability === 'unavailable' && isAvailable) return false;
    }
    
    // Image filter
    if (hasImage !== 'all') {
      const hasProductImage = !!product.first_photo_url;
      if (hasImage === 'with-image' && !hasProductImage) return false;
      if (hasImage === 'without-image' && hasProductImage) return false;
    }
    
    // Model filter
    if (model && model !== 'all') {
      if (product.model !== model) return false;
    }
    
    if (status !== 'all') {
      const isActive = product.active === true;  // Changed from product.status === 'active'
      if (status === 'active' && !isActive) return false;
      if (status === 'inactive' && isActive) return false;
    }
    
    return true;
  });
};

const sortProducts = (products, sortBy) => {
  const sorted = [...products];
  switch (sortBy) {
    case 'title_asc':
      return sorted.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    case 'title_desc':
      return sorted.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
    case 'netlab_id_asc':
      return sorted.sort((a, b) => (a.netlab_id || '').localeCompare(b.netlab_id || ''));
    case 'netlab_id_desc':
      return sorted.sort((a, b) => (b.netlab_id || '').localeCompare(a.netlab_id || ''));
    default:
      return sorted;
  }
};

// Get unique models from products for dropdown
const getUniqueModels = (products) => {
  const models = new Set();
  products.forEach(product => {
    if (product.model) {
      models.add(product.model);
    }
  });
  return Array.from(models).sort();
};

// ========== HEADER COMPONENTS ==========
const PageHeader = ({ productsCount, totalCount, hasActiveFilters, onNewProduct, onLogout }) => (
  <div className="mb-6 flex justify-between items-center">
    <div>
      <h1 className="text-2xl font-bold text-gray-900">Products</h1>
      <p className="mt-1 text-sm text-gray-500">
        {hasActiveFilters ? (
          <>
            {productsCount} of {totalCount} products found
          </>
        ) : (
          <>
            {productsCount} product{productsCount === 1 ? '' : 's'} found
          </>
        )}
      </p>
    </div>
    <div className="flex items-center gap-2">
      <Button
        onClick={onNewProduct}
        className="bg-blue-600 hover:bg-blue-700 text-white"
      >
        <Plus className="h-4 w-4 mr-2" />
        New Product
      </Button>
      <Button
        onClick={onLogout}
        variant="outline"
        className="border-gray-300 text-gray-700 hover:bg-gray-50"
      >
        <LogOut className="h-4 w-4 mr-2" />
        Logout
      </Button>
    </div>
  </div>
);

// ========== SEARCH & FILTER COMPONENTS ==========
const SearchBar = ({ value, onChange }) => (
  <div className="relative">
    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
      <Search className="h-5 w-5 text-gray-400" />
    </div>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
      placeholder="Search products by title, description, Netlab ID or SKU..."
    />
  </div>
);

const ViewToggle = ({ view, onViewChange }) => (
  <div className="flex items-center gap-2 border rounded-lg overflow-hidden">
    <button
      onClick={() => onViewChange('grid')}
      className={`p-2 ${
        view === 'grid'
        ? 'bg-blue-50 text-blue-600'
        : 'hover:bg-gray-50 text-gray-600'
      }`}
      aria-label="Grid view"
    >
      <LayoutGrid className="h-5 w-5" />
    </button>
    <button
      onClick={() => onViewChange('list')}
      className={`p-2 ${
        view === 'list'
        ? 'bg-blue-50 text-blue-600'
        : 'hover:bg-gray-50 text-gray-600'
      }`}
      aria-label="List view"
    >
      <List className="h-5 w-5" />
    </button>
  </div>
);

// New FilterBar component
const FilterBar = ({ 
  filters, 
  onFilterChange, 
  onSortChange, 
  sortBy,
  uniqueModels = [],
  activeFilterCount 
}) => {
  const handleFilterChange = (filterName, value) => {
    onFilterChange(filterName, value);
  };

  const resetFilters = () => {
    onFilterChange('reset', null);
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Sort Dropdown */}
      <div className="flex items-center">
        <select
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value)}
          className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
        >
          <option value="">Sort by</option>
          <option value="title_asc">Title (A-Z)</option>
          <option value="title_desc">Title (Z-A)</option>
          <option value="netlab_id_asc">Netlab ID (Low-High)</option>
          <option value="netlab_id_desc">Netlab ID (High-Low)</option>
        </select>
      </div>

      <div className="h-6 w-px bg-gray-300" />

      {/* Availability Filter */}
      <select
        value={filters.availability}
        onChange={(e) => handleFilterChange('availability', e.target.value)}
        className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
      >
        <option value="all">All Availability</option>
        <option value="available">In Stock</option>
        <option value="unavailable">Out of Stock</option>
      </select>

      {/* Image Filter */}
      <select
        value={filters.hasImage}
        onChange={(e) => handleFilterChange('hasImage', e.target.value)}
        className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
      >
        <option value="all">All Products</option>
        <option value="with-image">With Image</option>
        <option value="without-image">Without Image</option>
      </select>

      {/* Model Filter */}
      {uniqueModels.length > 0 && (
        <select
          value={filters.model}
          onChange={(e) => handleFilterChange('model', e.target.value)}
          className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
        >
          <option value="all">All Models</option>
          {uniqueModels.map(model => (
            <option key={model} value={model}>{model}</option>
          ))}
        </select>
      )}

      {/* Status Filter */}
      <select
        value={filters.status}
        onChange={(e) => handleFilterChange('status', e.target.value)}
        className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
      >
        <option value="all">All Status</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
      </select>

      {/* Clear Filters Button */}
      {activeFilterCount > 0 && (
        <button
          onClick={resetFilters}
          className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
        >
          <X className="h-4 w-4" />
          Clear filters ({activeFilterCount})
        </button>
      )}
    </div>
  );
};

// New FilterChips component
const FilterChips = ({ filters, onRemoveFilter }) => {
  const activeFilters = [];
  
  if (filters.availability !== 'all') {
    activeFilters.push({
      key: 'availability',
      label: `Stock: ${filters.availability === 'available' ? 'In Stock' : 'Out of Stock'}`
    });
  }
  
  if (filters.hasImage !== 'all') {
    activeFilters.push({
      key: 'hasImage',
      label: filters.hasImage === 'with-image' ? 'Has Image' : 'No Image'
    });
  }
  
  if (filters.model && filters.model !== 'all') {
    activeFilters.push({
      key: 'model',
      label: `Model: ${filters.model}`
    });
  }
  
  if (filters.status !== 'all') {
    activeFilters.push({
      key: 'status',
      label: `Status: ${filters.status}`
    });
  }

  if (activeFilters.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {activeFilters.map(filter => (
        <div
          key={filter.key}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-blue-50 text-blue-700"
        >
          <span>{filter.label}</span>
          <button
            onClick={() => onRemoveFilter(filter.key)}
            className="hover:bg-blue-100 rounded-full p-0.5"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}
    </div>
  );
};

// Updated SearchAndFilters component
const SearchAndFilters = ({ 
  searchQuery, 
  onSearchChange, 
  view, 
  onViewChange, 
  filters,
  onFilterChange,
  sortBy,
  onSortChange,
  uniqueModels,
  activeFilterCount
}) => {
  return (
    <div className="mb-6 space-y-4">
      {/* Search and View Toggle Row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="sm:w-96">
          <SearchBar value={searchQuery} onChange={onSearchChange} />
        </div>
        <ViewToggle view={view} onViewChange={onViewChange} />
      </div>
      
      {/* Filters Row */}
      <FilterBar
        filters={filters}
        onFilterChange={onFilterChange}
        onSortChange={onSortChange}
        sortBy={sortBy}
        uniqueModels={uniqueModels}
        activeFilterCount={activeFilterCount}
      />
      
      {/* Active Filter Chips */}
      <FilterChips 
        filters={filters} 
        onRemoveFilter={(filterKey) => onFilterChange(filterKey, 'all')}
      />
    </div>
  );
};

// ========== PRODUCT DISPLAY COMPONENTS ==========
const ProductListItem = ({ product, onClick }) => (
  <div 
    onClick={() => onClick(product.id)}
    className="bg-white hover:bg-gray-50 transition-all rounded-lg mb-2 cursor-pointer group px-4"
  >
    <div className="flex items-center gap-4 py-4">
      <div className="w-16 h-16 flex-shrink-0 bg-gray-50 rounded-lg overflow-hidden">
        {product.first_photo_url ? (
          <img
            src={product.first_photo_url}
            alt={product.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="h-8 w-8 text-gray-300" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-sm font-medium text-gray-900 truncate">
            {displayValue(product.title, "Untitled Product")}
          </h3>
          {/* Status indicator dot with better visibility */}
          <div 
            className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ring-1 ring-white ${
              product.active ? 'bg-green-500' : 'bg-red-500'
            }`}
            title={product.active ? 'Active' : 'Inactive'}
          />
        </div>
        <p className="text-sm text-gray-500 truncate">
          Netlab ID: {displayValue(product.netlab_id)}
        </p>
        {product.sku && (
          <p className="text-sm text-gray-500 truncate">
            SKU: {displayValue(product.sku)}
          </p>
        )}
        <p className="text-sm text-gray-500 truncate">
          {displayValue(product.short_description, "No description available")}
        </p>
      </div>

      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <div className="text-sm text-gray-900">
          {product.price ? `€${(product.price / 100).toFixed(2)}` : "Price not set"}
        </div>
        <div className="text-sm text-gray-500">
          Stock: {displayValue(product.available_inventory, "Not tracked")}
        </div>
      </div>
    </div>
  </div>
);

const ProductCard = ({ product, onClick }) => (
  <div 
    onClick={() => onClick(product.id)}
    className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all cursor-pointer group overflow-hidden flex flex-col h-full"
  >
    <div className="aspect-square w-full bg-gray-50 relative">
      <div className="w-full h-full overflow-hidden">
        {product.first_photo_url ? (
          <img
            src={product.first_photo_url}
            alt={product.title || 'Product'}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <Package className="h-12 w-12 text-gray-400" />
          </div>
        )}
      </div>
      {/* Status indicator badge in top-right corner */}
      <div 
        className={`absolute top-2 right-2 w-3 h-3 rounded-full border-2 border-white shadow-md ${
          product.active ? 'bg-green-500' : 'bg-red-500'
        }`}
        title={product.active ? 'Active' : 'Inactive'}
      />
    </div>

    <div className="p-4 flex-1 flex flex-col">
      <div className="flex-1">
        <div className="text-xs text-gray-500 mb-1">
          Netlab ID: {displayValue(product.netlab_id)}
        </div>
        {product.sku && (
          <div className="text-xs text-gray-500 mb-1">
            SKU: {displayValue(product.sku)}
          </div>
        )}
        <h3 className="font-medium text-gray-900 mb-1 line-clamp-1">
          {displayValue(product.title, "Untitled Product")}
        </h3>
        <p className="text-sm text-gray-600 line-clamp-2 mb-3">
          {displayValue(product.short_description, "No description available")}
        </p>
      </div>
      
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="text-sm font-medium text-gray-900">
          {product.price ? `€${(product.price / 100).toFixed(2)}` : "Price not set"}
        </div>
        <div className="text-sm text-gray-500">
          Stock: {displayValue(product.available_inventory, "Not tracked")}
        </div>
      </div>
    </div>
  </div>
);

const ProductsGrid = ({ products, onProductClick }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
    {products.map((product) => (
      <ProductCard 
        key={product.id} 
        product={product} 
        onClick={onProductClick}
      />
    ))}
  </div>
);

const ProductsList = ({ products, onProductClick }) => (
  <div className="border-t border-gray-200">
    {products.map((product) => (
      <ProductListItem 
        key={product.id} 
        product={product} 
        onClick={onProductClick}
      />
    ))}
  </div>
);

const EmptyState = () => (
  <div className="text-center py-12">
    <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
    <p className="text-gray-500">Try adjusting your search or filters</p>
  </div>
);

// ========== LOADING & ERROR STATES ==========
const LoadingState = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="flex items-center gap-2">
      <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      <span className="text-gray-600">Loading products...</span>
    </div>
  </div>
);

const ErrorState = ({ error }) => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <h3 className="text-lg font-medium text-gray-900 mb-2">Something went wrong</h3>
      <p className="text-gray-500">{error}</p>
    </div>
  </div>
);

// ========== DIALOGS ==========
const UnsavedChangesDialog = ({ open, onConfirm, onCancel }) => (
  <AlertDialog open={open} onOpenChange={onCancel}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
        <AlertDialogDescription>
          You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm}>Leave</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

// ========== CUSTOM HOOKS ==========
const useProductsData = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { toast } = useToast();

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await productService.fetchProducts();
      setProducts(data);
    } catch (err) {
      setError(err.message);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load products. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return { products, loading, error, refetch: fetchProducts };
};

const useProductNavigation = (hasUnsavedChanges) => {
  const navigate = useNavigate();
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [pendingNavigationId, setPendingNavigationId] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);

  const navigateToProduct = useCallback((productId) => {
    setIsNavigating(true);
    navigate(productId ? `/products/${productId}` : '/products');
    setTimeout(() => setIsNavigating(false), 100);
  }, [navigate]);

  const handleNavigationAttempt = useCallback((destination) => {
    if (hasUnsavedChanges) {
      setPendingNavigationId(destination);
      setShowExitDialog(true);
    } else if (destination === 'dashboard') {
      navigate('/dashboard');
    } else {
      navigateToProduct(destination);
    }
  }, [hasUnsavedChanges, navigate, navigateToProduct]);

  const confirmNavigation = useCallback(() => {
    setShowExitDialog(false);
    if (pendingNavigationId === 'dashboard') {
      navigate('/dashboard');
    } else {
      navigateToProduct(pendingNavigationId);
    }
    setPendingNavigationId(null);
  }, [pendingNavigationId, navigate, navigateToProduct]);

  const cancelNavigation = useCallback(() => {
    setShowExitDialog(false);
    setPendingNavigationId(null);
  }, []);

  return {
    showExitDialog,
    isNavigating,
    handleNavigationAttempt,
    confirmNavigation,
    cancelNavigation,
    navigateToProduct
  };
};

// ========== MAIN COMPONENT ==========
const ProductsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { productId } = useParams();
  const { toast } = useToast();
  
  // State - Enhanced with filters
  const [filters, setFilters] = useState({
    searchQuery: '',
    availability: 'all',
    hasImage: 'all',
    model: 'all',
    status: 'all'
  });
  const [sortBy, setSortBy] = useState('');
  const [view, setView] = useState('grid');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  
  // Custom hooks
  const { products, loading, error, refetch } = useProductsData();
  const {
    showExitDialog,
    isNavigating,
    handleNavigationAttempt,
    confirmNavigation,
    cancelNavigation,
    navigateToProduct
  } = useProductNavigation(hasUnsavedChanges);

  // Get unique models from products
  const uniqueModels = useMemo(() => 
    getUniqueModels(products), 
    [products]
  );

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.availability !== 'all') count++;
    if (filters.hasImage !== 'all') count++;
    if (filters.model !== 'all') count++;
    if (filters.status !== 'all') count++;
    return count;
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    if (filterName === 'reset') {
      setFilters({
        searchQuery: filters.searchQuery, // Keep search query
        availability: 'all',
        hasImage: 'all',
        model: 'all',
        status: 'all'
      });
    } else if (filterName === 'searchQuery') {
      setFilters(prev => ({ ...prev, searchQuery: value }));
    } else {
      setFilters(prev => ({ ...prev, [filterName]: value }));
    }
  };

  // Computed values
  const isFormRoute = location.pathname.includes('/new') || !!productId;
  
  // Apply filters and sorting
  const processedProducts = useMemo(() => {
    const filtered = filterProducts(products, filters);
    return sortProducts(filtered, sortBy);
  }, [products, filters, sortBy]);

  // Effects
  useEffect(() => {
    setIsSheetOpen(isFormRoute);
  }, [isFormRoute]);

  // Handlers
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleBackToDashboard = () => {
    handleNavigationAttempt('dashboard');
  };

  const handleNewProduct = () => {
    handleNavigationAttempt('new');
  };

  const handleProductSelect = (selectedId) => {
    if (!isNavigating) {
      handleNavigationAttempt(selectedId);
    }
  };

  const handleFormClose = () => {
    if (!isNavigating) {
      handleNavigationAttempt(null);
    }
  };

  const handleProductSave = async (savedProduct) => {
    setHasUnsavedChanges(false);
    setIsSheetOpen(false);
    await refetch();
    navigate('/products');
    toast({
      title: "Success",
      description: "Product saved successfully",
    });
  };

  // Render states
  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      {/* Back button */}
      <div className="mb-4">
        <Button 
          onClick={handleBackToDashboard}
          variant="outline" 
          className="flex items-center text-gray-600 hover:text-gray-800"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Dashboard
        </Button>
      </div>

      {/* Header */}
      <PageHeader 
        productsCount={processedProducts.length}
        totalCount={products.length}
        hasActiveFilters={activeFilterCount > 0 || filters.searchQuery !== ''}
        onNewProduct={handleNewProduct}
        onLogout={handleLogout}
      />

      {/* Search and filters */}
      <SearchAndFilters
        searchQuery={filters.searchQuery}
        onSearchChange={(value) => handleFilterChange('searchQuery', value)}
        view={view}
        onViewChange={setView}
        filters={filters}
        onFilterChange={handleFilterChange}
        sortBy={sortBy}
        onSortChange={setSortBy}
        uniqueModels={uniqueModels}
        activeFilterCount={activeFilterCount}
      />

      {/* Products display */}
      {processedProducts.length === 0 ? (
        <EmptyState />
      ) : view === 'grid' ? (
        <ProductsGrid products={processedProducts} onProductClick={handleProductSelect} />
      ) : (
        <ProductsList products={processedProducts} onProductClick={handleProductSelect} />
      )}

      {/* Product form sheet */}
      <Sheet 
        open={isSheetOpen}
        onOpenChange={(open) => {
          if (!open && isSheetOpen && !isNavigating) {
            handleFormClose();
          }
        }}
      >
        <SheetContent 
          className="w-full sm:max-w-none sm:w-[60vw] overflow-y-auto"
          side="right"
        >
          <SheetHeader>
            <SheetTitle>
              {productId === 'new' ? 'New Product' : 'Edit Product'}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-6">
            <ProductForm
              productId={productId}
              onSave={handleProductSave}
              onCancel={handleFormClose}
              onChange={setHasUnsavedChanges}
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Unsaved changes dialog */}
      <UnsavedChangesDialog
        open={showExitDialog}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
      />
    </div>
  );
};

export default ProductsPage;